import { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import type { DocumentAddress, StructuredPartyListItem } from './types';
import style from './panel-content.module.scss';

interface Props {
  open: boolean;
  address: DocumentAddress | null;
  parties: StructuredPartyListItem[];
  onClose: () => void;
  onSubmit: (partyIds: number[]) => Promise<void>;
}

export const ResolveToPartyPanel: FC<Props> = ({
  open,
  address,
  parties,
  onClose,
  onSubmit,
}) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const pageContentSelector = useExclusiveDrawer(
    'resolve-to-party',
    open,
    onClose
  );

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  const eligibleParties = useMemo(
    () =>
      parties.filter((p) => p.status !== 'removed' && p.status !== 'complete'),
    [parties]
  );

  const handleToggle = (partyId: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(partyId);
      else next.delete(partyId);
      return next;
    });
  };

  const handleSave = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await onSubmit(Array.from(selected));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Resolve to Party"
      subtitle={
        address
          ? `Move "${
              address.fullText || address.asFoundAddress
            }" to one or more parties.`
          : ''
      }
      placement="right"
      size="md"
      slideIn
      selectorPageContent={pageContentSelector}
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: submitting ? 'Saving…' : 'Save',
          disabled: selected.size === 0 || submitting,
          onClick: handleSave,
        },
      ]}
    >
      <div className={style.panelBody}>
        {eligibleParties.length === 0 ? (
          <p>
            No eligible parties on this document. Parties marked Complete or
            Removed cannot receive resolved addresses — remove the completed
            status first.
          </p>
        ) : (
          eligibleParties.map((party) => (
            <Checkbox
              key={party.id}
              id={`resolve-party-${party.id}`}
              labelText={`${party.name || 'Unnamed party'}${
                party.role ? ` · ${party.role}` : ''
              }`}
              checked={selected.has(party.id)}
              onChange={(_e, { checked }: { checked: boolean }) =>
                handleToggle(party.id, checked)
              }
            />
          ))
        )}
      </div>
    </SidePanel>
  );
};
